/**
 * Marker creation and management for pub locations
 */

import L from 'leaflet';
import 'leaflet.markercluster';
import type { Pub, PubDatabase } from './types.js';
import { buildDirectionsLinks } from './directions.js';

export type PubStatus = 'visited' | 'unvisited' | 'closed';

const markerStatuses = new WeakMap<L.Marker, PubStatus>();

/**
 * Creates one of the three shared, status-specific marker icons.
 */
function createStatusIcon(status: PubStatus): L.DivIcon {
  const statusGlyph = {
    visited: '<path d="m11.5 15.3 3 3 6-7" />',
    unvisited: '<circle cx="16" cy="15" r="2.75" class="pub-marker__dot" />',
    closed: '<path d="m12 11 8 8m0-8-8 8" />',
  }[status];

  return L.divIcon({
    className: `pub-marker pub-marker--${status}`,
    html: `
      <span class="pub-marker__visual" aria-hidden="true">
        <svg viewBox="0 0 32 42" focusable="false">
          <path class="pub-marker__shape" d="M16 1C8.27 1 2 7.27 2 15c0 10.5 14 26 14 26s14-15.5 14-26C30 7.27 23.73 1 16 1Z" />
          <circle class="pub-marker__centre" cx="16" cy="15" r="8" />
          <g class="pub-marker__glyph">${statusGlyph}</g>
        </svg>
      </span>`,
    iconSize: [32, 42],
    iconAnchor: [16, 41],
    popupAnchor: [0, -38],
    tooltipAnchor: [0, -36],
  });
}

const STATUS_ICONS: Record<PubStatus, L.DivIcon> = {
  visited: createStatusIcon('visited'),
  unvisited: createStatusIcon('unvisited'),
  closed: createStatusIcon('closed'),
};

/**
 * Determines the marker status for a pub.
 * @param pub - The pub object
 * @returns Status used for its shared icon and cluster totals
 */
function getPubStatus(pub: Pub): PubStatus {
  if (pub.closed) {
    return 'closed';
  }
  if (pub.visited) {
    return 'visited';
  }
  return 'unvisited';
}

/** Returns the status metadata associated with a marker. */
export function getMarkerStatus(marker: L.Marker): PubStatus {
  return markerStatuses.get(marker) ?? 'unvisited';
}

/**
 * Escapes text for safe interpolation into popup HTML.
 * @param value - Raw text from the pub database
 * @returns The same text with HTML-significant characters escaped
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Reads the current user agent, falling back to an empty string off-browser.
 * @returns navigator.userAgent, or '' when unavailable
 */
function currentUserAgent(): string {
  return typeof navigator === 'undefined' ? '' : navigator.userAgent;
}

/**
 * Builds the driving-directions section of a popup.
 * @param pub - The pub object
 * @param userAgent - User agent string, used to order the providers
 * @returns HTML string, or '' when the pub has no usable coordinates
 */
function createDirectionsHtml(pub: Pub, userAgent: string): string {
  const links = buildDirectionsLinks(pub, userAgent);
  if (links.length === 0) {
    return '';
  }

  const buttons = links
    .map(
      (link) =>
        `<a class="pub-directions__link pub-directions__link--${link.provider}" href="${escapeHtml(
          link.url
        )}" target="_blank" rel="noopener noreferrer" aria-label="Driving directions to ${escapeHtml(
          pub.name
        )} in ${escapeHtml(link.label)}">${escapeHtml(link.label)}</a>`
    )
    .join('');

  return `
      <div class="pub-directions">
        <p class="pub-directions__title">Driving directions</p>
        <div class="pub-directions__links">${buttons}</div>
      </div>`;
}

/**
 * Creates HTML content for a pub marker popup
 * @param pub - The pub object
 * @param userAgent - User agent string, used to order the directions providers
 * @returns HTML string for popup content
 */
export function createMarkerPopup(pub: Pub, userAgent: string = currentUserAgent()): string {
  // Build status section
  let statusHtml = '';
  if (pub.closed) {
    statusHtml = '<p class="pub-status closed">Closed</p>';
    if (pub.closedDate) {
      statusHtml += `<p class="visit-dates">Closed: ${escapeHtml(pub.closedDate)}</p>`;
    }
  } else if (pub.visited) {
    statusHtml = '<p class="pub-status visited">Visited</p>';
    if (pub.firstVisit && pub.lastVisit) {
      if (pub.firstVisit === pub.lastVisit) {
        statusHtml += `<p class="visit-dates">Visited: ${escapeHtml(pub.firstVisit)}</p>`;
      } else {
        statusHtml += `<p class="visit-dates">First visit: ${escapeHtml(pub.firstVisit)}</p>`;
        statusHtml += `<p class="visit-dates">Last visit: ${escapeHtml(pub.lastVisit)}</p>`;
      }
    }
  } else {
    statusHtml = '<p class="pub-status unvisited">Not visited yet</p>';
  }

  return `
    <div class="pub-popup">
      <h3>${escapeHtml(pub.name)}</h3>
      <p>${escapeHtml(pub.address)}</p>
      <p>${escapeHtml(pub.town)}</p>
      <p>${escapeHtml(pub.postcode)}</p>
      ${statusHtml}
      ${createDirectionsHtml(pub, userAgent)}
    </div>
  `;
}

/**
 * Creates a Leaflet marker for a pub
 * @param pub - The pub object
 * @returns Leaflet marker
 */
export function createPubMarker(pub: Pub): L.Marker {
  const status = getPubStatus(pub);

  const marker = L.marker([pub.lat, pub.lng], {
    icon: STATUS_ICONS[status],
    alt: `${pub.name} — ${status}`,
    title: pub.name,
    riseOnHover: true,
  });
  markerStatuses.set(marker, status);

  // Bind popup with pub details
  const popupContent = createMarkerPopup(pub);
  marker.bindPopup(popupContent);

  // Bind tooltip for hover
  marker.bindTooltip(pub.name, {
    direction: 'top',
  });

  return marker;
}

/**
 * Creates markers for all pubs (without adding to map)
 * @param pubs - Array of pub objects
 * @returns Array of created markers
 */
export function createPubMarkers(pubs: PubDatabase): L.Marker[] {
  console.log(`Creating markers for ${pubs.length} pubs...`);

  const markers = pubs.map((pub) => createPubMarker(pub));

  console.log(`Created ${markers.length} markers`);

  return markers;
}

/**
 * Creates markers for all pubs and adds them to the map with clustering
 * @deprecated Use createPubMarkers() with ClusterManager instead
 * @param pubs - Array of pub objects
 * @param map - Leaflet map instance
 * @returns Array of created markers
 */
export function addPubMarkersToMap(pubs: PubDatabase, map: L.Map): L.Marker[] {
  console.log(`Creating markers for ${pubs.length} pubs...`);

  // Create marker cluster group
  const markerClusterGroup = L.markerClusterGroup({
    // Cluster settings
    spiderfyOnMaxZoom: true,
    showCoverageOnHover: false,
    zoomToBoundsOnClick: true,
    maxClusterRadius: 80,
  });

  // Create and batch-add markers to avoid repeated cluster-tree updates.
  const markers = createPubMarkers(pubs);
  markerClusterGroup.addLayers(markers);

  // Add cluster group to map
  map.addLayer(markerClusterGroup);

  console.log(`Added ${markers.length} markers to map with clustering`);

  return markers;
}
